import React, { useEffect, useState } from 'react';
import { Box, Typography, useTheme } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const UsersList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  
  const [users, setUsers] = useState({ results: [], count: 0 });
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  const fetchUsers = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`https://cheebo-server.neuralbey.com/dashboard/users/usersList/?page=${page}`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(paginationModel.page + 1);
  }, [paginationModel.page]);

  const handleUsernameClick = (user) => {
    navigate(`/userDetails/${user.id}`, { state: { user } });
  };

  const columns = [
    { 
      field: "id", 
      headerName: "ID", 
      flex: 0.5 
    },
    {
      field: "profile_image",
      headerName: "Profile",
      width: 100,
      renderCell: ({ row }) => {
        const profileImage = row.profile?.profile_image ? 
          `https://cheebo-server.neuralbey.com${row.profile.profile_image}` : 
          "/default-avatar.png";
        
        return (
          <img 
            src={profileImage} 
            alt={`${row.username}'s profile`} 
            style={{ 
              width: '50px', 
              height: '50px', 
              borderRadius: '4px',
              objectFit: 'cover'
            }} 
          />
        );
      }
    },
    {
      field: "username",
      headerName: "Username",
      flex: 1,
      cellClassName: "name-column--cell",
      renderCell: (params) => (
        <Typography
          color={colors.greenAccent[300]}
          sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => handleUsernameClick(params.row)}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
    },
    {
      field: "first_name",
      headerName: "First Name",
      flex: 1,
    },
    {
      field: "last_name",
      headerName: "Last Name",
      flex: 1,
    },
    {
      field: "date_joined",
      headerName: "Joined Date",
      flex: 1,
      renderCell: (params) => {
        return new Date(params.row.date_joined).toLocaleDateString();
      }
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 0.8,
      renderCell: ({ row: { is_active } }) => {
        return (
          <Box
            width="60%"
            m="0 auto"
            p="5px"
            display="flex"
            justifyContent="center"
            backgroundColor={
              is_active === true
                ? colors.greenAccent[600]
                : colors.redAccent[700]
            }
            borderRadius="4px"
          >
            <Typography color={colors.grey[100]} sx={{ ml: "5px" }}>
              {is_active ? "Active" : "Inactive"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "access_level",
      headerName: "Access Level",
      flex: 1,
      renderCell: ({ row: { access_level } }) => {
        return (
          <Box
            width="60%"
            m="0 auto"
            p="5px"
            display="flex"
            justifyContent="center"
            backgroundColor={
              access_level === "admin"
                ? colors.redAccent[600]
                : access_level === "manager"
                ? colors.blueAccent[700]
                : colors.greenAccent[700]
            }
            borderRadius="4px"
          >
            {access_level === "admin" && <AdminPanelSettingsOutlinedIcon />}
            {access_level === "manager" && <SecurityOutlinedIcon />}
            {access_level === "user" && <LockOpenOutlinedIcon />}
            <Typography color={colors.grey[100]} sx={{ ml: "5px" }}>
              {access_level}
            </Typography>
          </Box>
        );
      },
    },
  ];

  return (
    <Box m="20px">
      <Header
        title="USERS"
        subtitle="Managing the Users List"
      />
      <Box mt="10px">
        <Typography variant="h5" color={colors.greenAccent[400]}>
          Total Users: {users.count}
        </Typography>
      </Box>
      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={users.results}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          paginationMode="server"
          rowCount={users.count}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          loading={loading}
          pageSizeOptions={[10]}
        />
      </Box>
    </Box>
  );
};

export default UsersList;